import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import aa from "../components/Clinic/AdminPortal/adminApp.module.css";

const PULL_THRESHOLD = 80;
const PULL_ACTIVATION = 18;
const MAX_PULL = 100;

function findScrollRoot() {
  if (typeof document === "undefined") return null;
  const marked = [...document.querySelectorAll("[data-pull-scroll]")];
  if (!marked.length) return document.scrollingElement || document.documentElement;

  let deepest = null;
  let deepestDepth = -1;

  for (const el of marked) {
    const { overflowY } = window.getComputedStyle(el);
    const canScroll =
      (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
      el.scrollHeight > el.clientHeight + 2;

    const depth = (() => {
      let d = 0;
      let node = el;
      while (node.parentElement) {
        d += 1;
        node = node.parentElement;
      }
      return d;
    })();

    if (canScroll && depth > deepestDepth) {
      deepest = el;
      deepestDepth = depth;
    }
  }

  if (deepest) return deepest;
  return marked[marked.length - 1];
}

function scrollTopOf(el) {
  if (!el) return 0;
  return el.scrollTop ?? 0;
}

function isScrollLocked() {
  if (typeof document === "undefined") return false;
  return (
    document.body.style.overflow === "hidden" ||
    document.documentElement.classList.contains("hub-sheet-open")
  );
}

function isInsideBlockingOverlay(target) {
  if (!target?.closest) return false;
  return Boolean(
    target.closest(
      '[aria-modal="true"], [role="dialog"], [role="alertdialog"], [data-no-pull-refresh]',
    ),
  );
}

function findScrollableAncestor(el, stopAt) {
  let node = el;
  while (node && node !== stopAt && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight + 2
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

export function usePullToRefresh(onRefresh, { enabled = true } = {}) {
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const startY = useRef(0);
  const startX = useRef(0);
  const active = useRef(false);
  const armed = useRef(false);
  const refreshing = useRef(false);
  const pullDistanceRef = useRef(0);
  const scrollRootRef = useRef(null);

  const resetPull = useCallback(() => {
    armed.current = false;
    pullDistanceRef.current = 0;
    setPullDistance(0);
  }, []);

  const finishRefresh = useCallback(async () => {
    if (refreshing.current) return;
    refreshing.current = true;
    setPulling(true);
    setPullDistance(PULL_THRESHOLD);
    pullDistanceRef.current = PULL_THRESHOLD;
    try {
      await onRefresh?.();
    } finally {
      await new Promise((r) => setTimeout(r, 320));
      refreshing.current = false;
      setPulling(false);
      resetPull();
    }
  }, [onRefresh, resetPull]);

  useEffect(() => {
    if (!enabled) return undefined;

    const getRoot = () => {
      const root = findScrollRoot();
      scrollRootRef.current = root;
      return root;
    };

    const onTouchStart = (e) => {
      if (refreshing.current || isScrollLocked()) return;
      if (isInsideBlockingOverlay(e.target)) return;
      const root = getRoot();
      if (scrollTopOf(root) > 6) return;
      startY.current = e.touches[0].clientY;
      startX.current = e.touches[0].clientX;
      active.current = true;
      armed.current = false;
    };

    const onTouchMove = (e) => {
      if (!active.current || refreshing.current) return;
      if (isScrollLocked() || isInsideBlockingOverlay(e.target)) {
        active.current = false;
        resetPull();
        return;
      }
      const root = scrollRootRef.current || getRoot();

      if (scrollTopOf(root) > 6) {
        active.current = false;
        resetPull();
        return;
      }

      const dy = e.touches[0].clientY - startY.current;
      const dx = e.touches[0].clientX - startX.current;

      const nested = findScrollableAncestor(e.target, root);
      if (nested && nested !== root) {
        const nestedTop = scrollTopOf(nested);
        const nestedMax = nested.scrollHeight - nested.clientHeight;
        if (dy > 0 && nestedTop > 2) {
          active.current = false;
          resetPull();
          return;
        }
        if (dy < 0 && nestedTop < nestedMax - 2) {
          active.current = false;
          resetPull();
          return;
        }
      }

      if (dy < 0) {
        active.current = false;
        resetPull();
        return;
      }

      if (Math.abs(dx) > Math.abs(dy) + 8) {
        active.current = false;
        resetPull();
        return;
      }

      if (dy < PULL_ACTIVATION) {
        return;
      }

      armed.current = true;
      const dist = Math.min((dy - PULL_ACTIVATION) * 0.45 + PULL_ACTIVATION * 0.3, MAX_PULL);
      pullDistanceRef.current = dist;
      setPullDistance(dist);

      if (armed.current && e.cancelable) {
        e.preventDefault();
      }
    };

    const onTouchEnd = () => {
      if (!active.current) return;
      active.current = false;
      if (armed.current && pullDistanceRef.current >= PULL_THRESHOLD) {
        finishRefresh();
      } else {
        resetPull();
      }
      armed.current = false;
    };

    const root = getRoot();
    const target = typeof document !== "undefined" ? document : window;
    target.addEventListener("touchstart", onTouchStart, { passive: true });
    target.addEventListener("touchmove", onTouchMove, { passive: false });
    target.addEventListener("touchend", onTouchEnd);
    target.addEventListener("touchcancel", onTouchEnd);

    return () => {
      target.removeEventListener("touchstart", onTouchStart);
      target.removeEventListener("touchmove", onTouchMove);
      target.removeEventListener("touchend", onTouchEnd);
      target.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [enabled, finishRefresh, resetPull]);

  return { pulling, pullDistance, threshold: PULL_THRESHOLD };
}

export function PullToRefreshIndicator({ pulling, pullDistance, threshold = PULL_THRESHOLD }) {
  const visible = pulling || pullDistance > 4;
  if (!visible || typeof document === "undefined") return null;

  const progress = pulling ? 1 : Math.min(pullDistance / threshold, 1);
  const label = pulling
    ? "Обновление…"
    : pullDistance >= threshold
      ? "Отпустите"
      : "Потяните вниз";

  const node = (
    <div
      className={aa.ptrOverlay}
      style={{
        opacity: Math.max(0.4, progress),
        transform: `translateY(${pulling ? 0 : Math.max(-8, pullDistance - 20)}px)`,
      }}
      aria-live="polite"
      aria-hidden={!visible}
    >
      <div className={aa.ptrOverlayInner}>
        <div
          className={`${aa.ptrSpinner} ${pulling ? aa.ptrSpinnerOn : ""}`}
          style={{ transform: `rotate(${progress * 320}deg)` }}
        />
        <span className={aa.ptrLabel}>{label}</span>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
